// Page "Projets" : liste filtrable par client + KPI + création/édition/suppression.

import { FormEvent, useEffect, useMemo, useState } from "react";

import { clientsApi } from "../api/clients";
import { ApiError } from "../api/client";
import { projectsApi } from "../api/projects";
import { invoicesApi } from "../api/invoices";
import type {
  ClientPublic,
  InvoicePublic,
  ProjectCreate,
  ProjectPublic,
  ProjectUpdate,
  UUID,
} from "../api/types";
import { useTranslation } from "react-i18next";
import { AppShell } from "../components/AppShell";
import { FormField } from "../components/FormField";
import { Modal } from "../components/Modal";

type DialogState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; project: ProjectPublic };

function fmt(amount: number, currency = "EUR") {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
}

export function ProjectsPage() {
  const { t } = useTranslation();
  const [projects, setProjects] = useState<ProjectPublic[] | null>(null);
  const [clients, setClients] = useState<ClientPublic[]>([]);
  const [invoices, setInvoices] = useState<InvoicePublic[]>([]);
  const [filterClient, setFilterClient] = useState<UUID | "">("");
  const [listError, setListError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });

  const clientsById = useMemo(() => {
    const map = new Map<UUID, ClientPublic>();
    for (const c of clients) map.set(c.id, c);
    return map;
  }, [clients]);

  // KPI globaux (tous projets confondus)
  const kpi = useMemo(() => {
    const byProject = new Map<UUID, { total: number; paid: number; pending: number; nb: number }>();
    for (const inv of invoices) {
      if (!inv.project_id) continue;
      const ttc = parseFloat(inv.amount_ttc as unknown as string) || 0;
      const cur = byProject.get(inv.project_id) ?? { total: 0, paid: 0, pending: 0, nb: 0 };
      cur.total += ttc;
      if (inv.status === "paid") cur.paid += ttc;
      if (inv.status === "issued" || inv.status === "overdue") cur.pending += ttc;
      cur.nb += 1;
      byProject.set(inv.project_id, cur);
    }
    const totalCA = Array.from(byProject.values()).reduce((s, v) => s + v.total, 0);
    const totalPaid = Array.from(byProject.values()).reduce((s, v) => s + v.paid, 0);
    const totalPending = Array.from(byProject.values()).reduce((s, v) => s + v.pending, 0);
    const nbActifs = Array.from(byProject.values()).filter(v => v.nb > 0).size ?? byProject.size;
    return { totalCA, totalPaid, totalPending, nbProjets: projects?.length ?? 0, nbActifs };
  }, [invoices, projects]);

  // KPI par projet (pour enrichir le tableau)
  const invoicesByProject = useMemo(() => {
    const map = new Map<UUID, { total: number; paid: number; pending: number; nb: number }>();
    for (const inv of invoices) {
      if (!inv.project_id) continue;
      const ttc = parseFloat(inv.amount_ttc as unknown as string) || 0;
      const cur = map.get(inv.project_id) ?? { total: 0, paid: 0, pending: 0, nb: 0 };
      cur.total += ttc;
      if (inv.status === "paid") cur.paid += ttc;
      if (inv.status === "issued" || inv.status === "overdue") cur.pending += ttc;
      cur.nb += 1;
      map.set(inv.project_id, cur);
    }
    return map;
  }, [invoices]);

  async function reload() {
    setListError(null);
    try {
      const [p, c, inv] = await Promise.all([
        projectsApi.list(filterClient ? { client_id: filterClient } : {}),
        clientsApi.list({ limit: 500 }),
        invoicesApi.list({ limit: 200 }),
      ]);
      setProjects(p);
      setClients(c);
      setInvoices(inv.items ?? (inv as unknown as InvoicePublic[]));
    } catch (err) {
      setListError(err instanceof Error ? err.message : "Erreur");
      setProjects([]);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterClient]);

  async function onDelete(p: ProjectPublic) {
    if (!window.confirm(t("projectsPage.deleteConfirm").replace("{name}", p.name))) return;
    try {
      await projectsApi.delete(p.id);
      await reload();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Suppression impossible";
      window.alert(msg);
    }
  }

  return (
    <AppShell>
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{t("projectsPage.title")}</h1>
          <p className="text-sm text-slate-500">{t("projectsPage.subtitle")}</p>
        </div>
        <button
          type="button"
          className="btn-primary"
          disabled={clients.length === 0}
          onClick={() => setDialog({ mode: "create" })}
        >
          + {t("projectsPage.newProject")}
        </button>
      </header>

      {/* KPI GLOBAUX */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs text-slate-500 mb-1">{t("projectsPage.nbInvoices")}</p>
          <p className="text-2xl font-bold text-slate-900">{kpi.nbProjets}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs text-slate-500 mb-1">{t("projectsPage.caTotal")}</p>
          <p className="text-2xl font-bold text-slate-900">{fmt(kpi.totalCA)}</p>
        </div>
        <div className="bg-white border border-emerald-200 rounded-xl p-4">
          <p className="text-xs text-emerald-600 mb-1">{t("projectsPage.collected")}</p>
          <p className="text-2xl font-bold text-emerald-700">{fmt(kpi.totalPaid)}</p>
        </div>
        <div className="bg-white border border-amber-200 rounded-xl p-4">
          <p className="text-xs text-amber-600 mb-1">{t("projectsPage.pending")}</p>
          <p className="text-2xl font-bold text-amber-700">{fmt(kpi.totalPending)}</p>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3 text-sm">
        <label className="text-slate-600" htmlFor="filter-client">
          {t("projectsPage.filterByClient")}
        </label>
        <select
          id="filter-client"
          value={filterClient}
          onChange={(e) => setFilterClient(e.target.value)}
          className="input max-w-xs"
        >
          <option value="">{t("projectsPage.allClients")}</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {listError && (
          <p role="alert" className="p-4 text-sm text-red-600">
            {listError}
          </p>
        )}
        {projects === null && (
          <p className="p-6 text-sm text-slate-500">Chargement…</p>
        )}
        {projects !== null && projects.length === 0 && !listError && (
          <p className="p-6 text-sm text-slate-500">
            {clients.length === 0
              ? t("projectsPage.noClients")
              : t("projectsPage.noProjects")}
          </p>
        )}
        {projects && projects.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">{t("common.name")}</th>
                <th className="px-4 py-2 font-medium">Référence</th>
                <th className="px-4 py-2 font-medium">Client</th>
                <th className="px-4 py-2 font-medium text-right">{t("projectsPage.nbInvoices")}</th>
                <th className="px-4 py-2 font-medium text-right">{t("projectsPage.caTotal")}</th>
                <th className="px-4 py-2 font-medium text-right">{t("projectsPage.collected")}</th>
                <th className="px-4 py-2 font-medium text-right">{t("projectsPage.pending")}</th>
                <th className="px-4 py-2 font-medium w-32 text-right">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => {
                const s = invoicesByProject.get(p.id) ?? { total: 0, paid: 0, pending: 0, nb: 0 };
                return (
                  <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2 font-medium text-slate-900">{p.name}</td>
                    <td className="px-4 py-2 text-slate-600">{p.reference ?? "—"}</td>
                    <td className="px-4 py-2 text-slate-600">{clientsById.get(p.client_id)?.name ?? "(inconnu)"}</td>
                    <td className="px-4 py-2 text-right text-slate-700">{s.nb}</td>
                    <td className="px-4 py-2 text-right font-semibold text-slate-800">{s.total ? fmt(s.total) : "—"}</td>
                    <td className="px-4 py-2 text-right text-emerald-700">{s.paid ? fmt(s.paid) : "—"}</td>
                    <td className="px-4 py-2 text-right text-amber-700">{s.pending ? fmt(s.pending) : "—"}</td>
                    <td className="px-4 py-2 text-right space-x-3">
                      <button type="button" className="text-brand-600 hover:underline" onClick={() => setDialog({ mode: "edit", project: p })}>{t("common.edit")}</button>
                      <button type="button" className="text-red-600 hover:underline" onClick={() => onDelete(p)}>{t("common.delete")}</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ProjectDialog
        state={dialog}
        clients={clients}
        onClose={() => setDialog({ mode: "closed" })}
        onSaved={async () => { setDialog({ mode: "closed" }); await reload(); }}
      />
    </AppShell>
  );
}

function ProjectDialog({ state, clients, onClose, onSaved }: {
  state: DialogState; clients: ClientPublic[]; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isOpen = state.mode !== "closed";
  const [values, setValues] = useState<ProjectCreate>({ client_id: clients[0]?.id ?? "", name: "", reference: null, description: null });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (state.mode === "edit") {
      setValues({ client_id: state.project.client_id, name: state.project.name, reference: state.project.reference, description: state.project.description });
    } else if (state.mode === "create") {
      setValues({ client_id: clients[0]?.id ?? "", name: "", reference: null, description: null });
    }
    setError(null);
  }, [state, clients]);

  if (!isOpen) return null;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      if (state.mode === "edit") {
        await projectsApi.update(state.project.id, { name: values.name.trim(), reference: nullIfEmpty(values.reference), description: nullIfEmpty(values.description) });
      } else {
        await projectsApi.create({ client_id: values.client_id, name: values.name.trim(), reference: nullIfEmpty(values.reference), description: nullIfEmpty(values.description) });
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError("Impossible d'enregistrer");
    } finally {
      setPending(false);
    }
  }

  const title = state.mode === "edit" ? t("projectsPage.editProject") : t("projectsPage.newProjectTitle");
  const isEdit = state.mode === "edit";

  return (
    <Modal open={isOpen} onClose={onClose} title={title}>
      <form onSubmit={onSubmit} className="space-y-4" aria-label="project-form">
        <FormField label="Client" htmlFor="client_id" required>
          <select id="client_id" required disabled={isEdit} value={values.client_id} onChange={(e) => setValues({ ...values, client_id: e.target.value })} className="input">
            {clients.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
          {isEdit && <p className="text-xs text-slate-400 mt-1">Un projet ne peut pas changer de client.</p>}
        </FormField>
        <FormField label={t("common.name")} htmlFor="name" required>
          <input id="name" required value={values.name} onChange={(e) => setValues({ ...values, name: e.target.value })} className="input" />
        </FormField>
        <FormField label="Référence" htmlFor="reference" hint="Numéro de PO, code interne…">
          <input id="reference" value={values.reference ?? ""} onChange={(e) => setValues({ ...values, reference: e.target.value || null })} className="input" />
        </FormField>
        <FormField label={t("common.description")} htmlFor="description">
          <textarea id="description" rows={3} value={values.description ?? ""} onChange={(e) => setValues({ ...values, description: e.target.value || null })} className="input" />
        </FormField>
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" className="text-slate-500 hover:text-slate-800 text-sm" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" disabled={pending} className="btn-primary">{pending ? t("common.saving") : t("common.save")}</button>
        </div>
      </form>
    </Modal>
  );
}

function nullIfEmpty(v: string | null | undefined): string | null {
  const t = (v ?? "").trim();
  return t ? t : null;
}
